import * as AgGrid from "ag-grid-community";
import * as columns from "grid/columns";
import * as datasource from "grid/datasource";
import * as gridUtils from "grid/utils";
import * as http from "utils/http";
import { ToggleAllRows } from "grid/toggleAllRows";
import {
  AMOUNT_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
  DEFAULT_COL_DEF,
  ACTIONS_COL_DEF,
  TEXT_FILTER_COL_DEF,
} from "grid/columns";
import { BulkAction } from "grid/bulk_action";
import { GridController } from "grid/grid_controller";
import { onActionCellClicked } from "grid/row_actions";
import { preventButtonDoubleClick } from "utils/dom";
import * as responses from "utils/responses";

enum Action {
  RETRIGGER = "retrigger",
  COMPLETE = "complete",
}

interface FailedAlfaTransactionReport {
  account_or_invoice_number: string;
  alfa_posting_date: string;
  alfa_transaction_id: string;
  id: string;
  jpm_receipt_number: string;
  manual_receipt_number: string;
  payment_amount: number;
  payment_date: string;
  status: Status;
}

enum Status {
  FAILED = "FAILED",
  SUCCESS = "SUCCESS",
}

function checkboxSelection(
  event:
    | AgGrid.CheckboxSelectionCallbackParams<FailedAlfaTransactionReport, any>
    | AgGrid.RowSelectedEvent<FailedAlfaTransactionReport, any>,
) {
  return event.data?.status == Status.FAILED;
}

export default class extends GridController<FailedAlfaTransactionReport> {
  static values = Object.assign(super.values, {
    bulkRetriggerUrl: String,
    isSelectionEnabled: Boolean,
  });

  declare readonly bulkRetriggerUrlValue: string;
  declare readonly isSelectionEnabledValue: boolean;

  bulkRetriggerAction: BulkAction<FailedAlfaTransactionReport>;

  override connect() {
    if (this.isSelectionEnabledValue) {
      const bulkRetriggerButton = document.querySelector(".bulk-retrigger") as HTMLElement;
      this.bulkRetriggerAction = new BulkAction(document.querySelector(".bulk-retrigger"));
      this.bulkRetriggerAction.onClick(
        preventButtonDoubleClick(bulkRetriggerButton, this.handleBulkRetrigger.bind(this)),
      );
    }
    super.connect();
  }

  override buildGrid() {
    const ctrl = this;
    const { alfaStatusesJson, i18nJson, paginationPageSize } = ctrl.gridTarget.dataset;
    const colDefs: AgGrid.ColDef[] = [
      ...(ctrl.isSelectionEnabledValue ? [{ width: 36, checkboxSelection, headerComponent: ToggleAllRows }] : []),
      { field: "alfa_transaction_id", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "account_or_invoice_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF },
      { field: "payment_date", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true, sort: "desc" },
      { field: "payment_amount", ...AMOUNT_COL_DEF, ...AMOUNT_FILTER_COL_DEF, editable: false },
      { field: "alfa_posting_date", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true },
      { field: "jpm_receipt_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "manual_receipt_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      {
        field: "status",
        ...DEFAULT_COL_DEF,
        ...columns.buildEnumColDef(JSON.parse(alfaStatusesJson)),
        ...columns.buildEnumFilterColDef(JSON.parse(alfaStatusesJson)),
        sortable: true,
      },
      {
        ...ACTIONS_COL_DEF,
        onCellClicked(params) {
          onActionCellClicked<Action>(params, async function (action) {
            switch (action) {
              case Action.COMPLETE:
                return await ctrl.handleComplete(params);
              case Action.RETRIGGER:
                return await ctrl.handleRetrigger(params);
            }
          });
        },
      },
    ];

    const dataSource = new datasource.Datasource({ callbacks: {}, limit: paginationPageSize });
    // prevent double-acting with checkbox
    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const options: AgGrid.GridOptions<FailedAlfaTransactionReport> = {
      ...defaultOptions,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<FailedAlfaTransactionReport, any>),
      },
      datasource: dataSource,
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n: JSON.parse(i18nJson) }),
      paginationPageSize: parseInt(paginationPageSize),
    };
    const selectionOptions: AgGrid.GridOptions<FailedAlfaTransactionReport> = ctrl.isSelectionEnabledValue
      ? {
          rowSelection: "multiple",
          suppressRowClickSelection: true,
          suppressRowDeselection: true,
          onRowSelected(event) {
            if (event.node.isSelected() && !checkboxSelection(event)) {
              event.node.setSelected(false);
            }
            ctrl.bulkRetriggerAction.onRowSelected(event);
          },
        }
      : {};
    const gridApi = AgGrid.createGrid(ctrl.gridTarget, { ...options, ...selectionOptions });
    dataSource.gridApi = gridApi;

    return gridApi;
  }

  async handleBulkRetrigger(reports: FailedAlfaTransactionReport[]) {
    await responses.notifyAboutFailureOrSuccess(
      await http.httpPost(this.bulkRetriggerUrlValue, {
        report_ids: reports.map((report) => report.id),
      }),
    );
    this.gridApi.purgeInfiniteCache();
    this.gridApi.deselectAll();
  }

  async handleComplete(params: AgGrid.CellClickedEvent) {
    await responses.notifyAboutFailureOrSuccess(await http.httpPost(params.data.complete_path));
    params.api.purgeInfiniteCache();
    params.node.setSelected(false);
  }

  async handleRetrigger(params: AgGrid.CellClickedEvent) {
    await responses.notifyAboutFailureOrSuccess(await http.httpPost(params.data.retrigger_path));
    params.api.purgeInfiniteCache();
    params.node.setSelected(false);
  }
}
